import React, { useState, useRef, useContext, useEffect } from 'react';
import { Popover, Overlay } from 'react-bootstrap'
import { UserContext } from 'shared/context/UserContext';
import { UserTypes } from "shared/constants/UserTypes";
import {
    GetParentCompanyRpidsForGroup,
    IsGroup,
} from 'shared/helpers/OrganizationDecomposition'
import { GetDraftCommit } from 'shared/helpers/OrganizationDraftSelection'
import OrganizationDropdownOption from './OrganizationDropdownOption'
import OrganizationDropdownFilter from './OrganizationDropdownFilter'
import './OrganizationDropdown.css'

export default function OrganizationDropdown(props) {
    const {
        onChange,
        optionRows,
        children,
        selectedOrganization,
        selectedRpids,
        onSelectedRpidsChange,
        isDecomposed
    } = props
    const userConfig = useContext(UserContext);
    const [show, setShow] = useState(false);
    const target = useRef(null);

    const [isFiltered, setIsFiltered] = useState(false)
    const [viewableRows, setViewableRows] = useState([]);
    const [filter, setFilter] = useState('');

    // The selection the user is building. Held here rather than written straight to context so
    // the dropdown can be browsed and revised freely: closing it is what makes the selection
    // final. Null means nothing is in flight, which is the state whenever it is closed.
    const [draft, setDraft] = useState(null)

    const committedRpids = Array.isArray(selectedRpids) ? selectedRpids : []

    // Everything the popover renders reads the draft, falling back to the committed selection
    // until the user touches something. That fallback is what lets the draft simply be dropped
    // whenever it stops being meaningful.
    const draftOrganization = draft ? draft.organization : selectedOrganization
    const draftRpids = draft ? draft.rpids : committedRpids

    const selectedOrganizationValue = draftOrganization ? draftOrganization.value : null
    const currentRpids = Array.isArray(draftRpids) ? draftRpids : []
    const isLastRemainingRpid = currentRpids.length === 1

    // A Group selected as a whole is narrowed to every parent company it resolves to, because the
    // group header has to render as fully checked. Organization.onOrganizationChange expands an
    // empty override to the same list, so both forms commit identically.
    function getWholeGroupRpids(organization) {
        return isDecomposed && IsGroup(organization)
            ? GetParentCompanyRpidsForGroup(organization)
            : []
    }

    // Closing the dropdown is the act of making the selection final, so this is the only place
    // that writes back. The commit is diff-based on purpose: onChange rewrites the cached
    // organization and clears the remembered NCPDP selection, so an open-and-close that changed
    // nothing has to reach context as nothing at all.
    function closeDropdown() {
        const commit = GetDraftCommit(draft, {
            organization: selectedOrganization,
            rpids: committedRpids
        })

        if (commit.kind === 'organization') {
            onChange(commit.organization, commit.rpids)
        } else if (commit.kind === 'rpids') {
            onSelectedRpidsChange(commit.rpids)
        }

        // The filter is a way of finding something, not part of the selection, so the next open
        // starts from the full list rather than a narrowed one the user has to explain to
        // themselves.
        setIsFiltered(false)
        setViewableRows([])
        setFilter('')

        setDraft(null)
        setShow(false)
    }

    function handleTriggerClick() {
        if (show) {
            closeDropdown()
            return
        }

        setShow(true)
    }

    // The dropdown trigger and the "decompose groups" option sit side by side inside this
    // container. Both are rendered outside the popover, so without this check rootClose would
    // treat clicking either as an outside click. The trigger closes the dropdown through its own
    // onClick; the decompose option is not a selection and leaves it open.
    function handleRootClose(event) {
        const clickedElement = event && event.target

        if (clickedElement
            && typeof clickedElement.closest === 'function'
            && clickedElement.closest('.Organization--header')) {
            return
        }

        closeDropdown()
    }

    // The filtered rows are a snapshot of the option list, so they go stale when that list is
    // rebuilt - which only happens when the decompose option is toggled. Clearing the filter is
    // the safe reset, and the draft is dropped for the same reason: that option rewrites the
    // narrowing in context and changes what the rows mean, so a draft built against the previous
    // shape can no longer be committed. Dropping it falls the popover back to the committed
    // selection, which is what the toggle just produced.
    useEffect(() => {
        setIsFiltered(false)
        setViewableRows([])
        setFilter('')
        setDraft(null)
    }, [optionRows])

    function getChildRowsOfGroup(groupValue) {
        return optionRows.filter(row => row.parentGroupValue === groupValue)
    }

    function getGroupByValue(groupValue) {
        const headerRow = optionRows.find(row => row.isGroupHeader && row.organization.value === groupValue)

        return headerRow ? headerRow.organization : null
    }

    function isSelectedGroup(groupValue) {
        return groupValue !== null && groupValue === selectedOrganizationValue
    }

    // Picking an organization replaces the draft selection. The popover stays open so the choice
    // can still be revised - it is not final until the dropdown closes.
    function onOrganizationChange(organization) {
        setDraft({ organization: organization, rpids: [] })
    }

    // A parent company row narrows the Group it sits under rather than replacing the selection,
    // so several can be toggled in one go.
    function onParentCompanyToggle(parentCompany, groupValue) {
        // Clicking into a group that is not the selected one switches to it and narrows it to
        // just the parent company that was clicked.
        if (!isSelectedGroup(groupValue)) {
            const group = getGroupByValue(groupValue)

            if (!group) return

            setDraft({ organization: group, rpids: [parentCompany.value] })
            return
        }

        if (!currentRpids.includes(parentCompany.value)) {
            setDraft({
                organization: draftOrganization,
                rpids: currentRpids.concat([parentCompany.value])
            })
            return
        }

        // A Group always resolves to at least one parent company, so clearing the last one would
        // leave the screen with nothing to show. Block the last removal.
        if (isLastRemainingRpid) return

        setDraft({
            organization: draftOrganization,
            rpids: currentRpids.filter(rpid => rpid !== parentCompany.value)
        })
    }

    // A group header selects the whole group. When it is already selected and narrowed, it acts
    // as a shortcut back to all of its parent companies.
    function onGroupToggle(group) {
        setDraft({ organization: group, rpids: getWholeGroupRpids(group) })
    }

    function handleFilterChange(filter) {
        setFilter(filter)

        if (filter === '') {
            setIsFiltered(false)
            //setViewableOptions([])
        } else {
            //Check if UTC user and filter accordingly
            var isUCTUser = userConfig.contactType === UserTypes.UCT;

            // Group headers are never matched directly - they are added back below for any group
            // that still has a visible child, so a matching parent company keeps its context.
            const selectableRows = optionRows.filter(row => !row.isGroupHeader)

            const matchedRows = selectableRows.filter(row => {
                const option = row.organization

                // Check label
                const labelMatch = option.label &&
                    option.label.toLowerCase().includes(filter.toLowerCase());

                if (!isUCTUser) return labelMatch

                // Check NCPDPList array
                const ncpdpMatch = option.NCPDPList &&
                    Array.isArray(option.NCPDPList) &&
                    option.NCPDPList.some(ncpdp =>
                        ncpdp.toLowerCase().includes(filter.toLowerCase())
                    );

                return labelMatch || ncpdpMatch;
            });

            setIsFiltered(true)
            setViewableRows(withGroupHeaders(matchedRows))
        }
    }

    // Re-inserts the group header above each surviving child, preserving the original ordering.
    function withGroupHeaders(matchedRows) {
        // Keyed by rowKey rather than by value: a parent company reachable through two groups has
        // the same value under both, and only the row that actually matched should survive.
        const matchedKeys = new Set(matchedRows.map(row => row.rowKey))
        const visibleGroupValues = new Set(
            matchedRows
                .filter(row => row.parentGroupValue !== null)
                .map(row => row.parentGroupValue)
        )

        return optionRows.filter(row => {
            if (row.isGroupHeader) {
                return visibleGroupValues.has(row.organization.value)
            }

            return matchedKeys.has(row.rowKey)
        })
    }

    let rowsToUse = isFiltered ? viewableRows : optionRows

    function getDropdownEntries() {
        return rowsToUse.map((row) => {
            const organization = row.organization

            if (row.isGroupHeader) {
                const childRows = getChildRowsOfGroup(organization.value)
                const isSelected = isSelectedGroup(organization.value)
                const selectedChildCount = isSelected
                    ? childRows.filter(childRow => currentRpids.includes(childRow.organization.value)).length
                    : 0

                return (
                    <OrganizationDropdownOption
                        key={row.rowKey}
                        option={organization}
                        onClick={onGroupToggle}
                        selected={isSelected && selectedChildCount === childRows.length}
                        partiallySelected={selectedChildCount > 0 && selectedChildCount < childRows.length}
                        isGroupHeader={true}
                        isEmptyGroup={childRows.length < 1}
                        depth={row.depth}
                    />
                )
            }

            // A parent company row beneath a group.
            if (row.parentGroupValue !== null) {
                const isGroupSelected = isSelectedGroup(row.parentGroupValue)
                const isSelected = isGroupSelected && currentRpids.includes(organization.value)

                return (
                    <OrganizationDropdownOption
                        key={row.rowKey}
                        option={organization}
                        onClick={(parentCompany) => onParentCompanyToggle(parentCompany, row.parentGroupValue)}
                        selected={isSelected}
                        disabled={isSelected && isLastRemainingRpid}
                        depth={row.depth}
                    />
                )
            }

            return (
                <OrganizationDropdownOption
                    key={row.rowKey}
                    option={organization}
                    onClick={onOrganizationChange}
                    selected={selectedOrganizationValue === organization.value}
                    depth={row.depth}
                />
            )
        })
    }

    const dropdownEntries = getDropdownEntries()

    const popover = (
        <Popover>
            <ul className="Organization-Dropdown--body">
                <OrganizationDropdownFilter onFilterChange={handleFilterChange} filter={filter} />
                {dropdownEntries}
            </ul>
        </Popover>
    )

    // rootClose handles outside clicks and Escape. It is wired up unconditionally so that the
    // decompose option, which sits inside the header but outside the popover, never counts as an
    // outside click - the header trigger closes the dropdown through its own onClick instead.
    return (
        <React.Fragment>
            <span onClick={handleTriggerClick} ref={target}>
                {children}
            </span>
            <Overlay
                target={target.current}
                show={show}
                placement="bottom"
                rootClose
                onHide={handleRootClose}>
                {popover}
            </Overlay>
        </React.Fragment>
    )
}
